import React, { useImperativeHandle, forwardRef, useState, useCallback } from 'react';
import ApiServices from '../../../../../platform/app/src/shared/ApiServices/ApiServices';
import { toast } from 'react-toastify';

export interface ICDCodeViewerProps {
    getText: () => string;
    onStatusChange?: (state: { loading: boolean; visible: boolean }) => void;
}

const ICDCodeViewer = forwardRef(({ getText, onStatusChange }: ICDCodeViewerProps, ref) => {
    const [codes, setCodes] = useState<any[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const updateStatus = useCallback(
        (status: { loading?: boolean; visible?: boolean }) => {
            const newStatus = {
                loading: status.loading ?? loading,
                visible: status.visible ?? visible,
            };
            setLoading(newStatus.loading);
            setVisible(newStatus.visible);
            onStatusChange?.(newStatus);
        },
        [loading, visible, onStatusChange]
    );

    useImperativeHandle(ref, () => ({
        async extract() {
            const text = getText();
            if (!text) {
                toast.warning('No text found in report.');
                return;
            }

            updateStatus({ loading: true, visible: false });
            setError(null);
            setCodes(null);

            try {
                const result = await ApiServices.getIcdCodesFromText(text);
                if (!result?.results || result.results.length === 0) {
                    setError('No ICD codes found.');
                    setCodes([]);
                } else {
                    setCodes(result.results);
                }
                updateStatus({ loading: false, visible: true });
            } catch (err: any) {
                const message = err?.response?.data?.message || 'Failed to extract ICD-10 codes.';
                toast.error(message);
                setError(message);
                setCodes(null);
                updateStatus({ loading: false, visible: true });
            }
        },
        hide() {
            updateStatus({ visible: false });
        },
        getStatus() {
            return { loading, visible };
        },
    }));

    if (!visible) return null;

    return (
        <div
            className="rounded-xl p-6 shadow-lg mb-6"
            style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}
        >
            {error ? (
                <div className="text-center text-red-600 text-sm font-semibold">{error}</div>
            ) : codes?.length === 0 ? (
                <div className="text-center text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>No ICD Codes Found.</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border-separate border-spacing-y-2">
                        <thead>
                            <tr className="text-white" style={{ background: '#045eb8' }}>
                                <th className="px-4 py-3 text-left rounded-l-lg">Term</th>
                                <th className="px-4 py-3 text-left">ICD Code</th>
                                <th className="px-4 py-3 text-left rounded-r-lg">Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {codes?.map((item, idx) => (
                                <tr
                                    key={idx}
                                    className="transition rounded-lg shadow-sm"
                                    style={{ borderColor: 'var(--border-subtle)' }}
                                >
                                    <td className="px-4 py-3 rounded-l-lg">{item.term}</td>
                                    <td className="px-4 py-3">{item.icd10_code}</td>
                                    <td className="px-4 py-3 rounded-r-lg">{item.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
});

export default ICDCodeViewer;
